import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import debounce from 'lodash.debounce';
import { PromiseState } from 'react-refetch';
import first from 'lodash.first';
import { useDispatch, useSelector } from 'react-redux';

import { EntityContext, EntityTypes } from 'context/entityContext';

import useMedia from 'hooks/useMedia';
import useCustomerFilters from 'hooks/useCustomerFilters';
import useExportURL from 'hooks/useExportURL';
import useOffset from 'hooks/useOffset';

import { ReactComponent as IconCheck } from 'icons/icon-checkmark.svg';

import { logToAmplitude } from 'helpers/logAmplitudeEvent';
import connect from 'helpers/apiConnect';
import getPlaylistTableURL from 'url-constructors/getPlaylistTableURL';
import getPlaylistPositionsTrendURL, {
  IPlaylistTrendURLArgs,
} from 'url-constructors/getPlaylistPositionsTrendURL';
import getPlaylistTrendURL from 'url-constructors/getPlaylistTrendURL';
import { saveToApi, loadRecentHeaders } from 'redux/reducers/user/browseTable';

import GenericComponent, {
  IToolbarItemType,
} from 'components/utility/GenericComponent/GenericComponent';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';
import Pagination from './components/Pagination';
import AnimatedLoadingWrapper from 'components/base/AnimatedLoadingWrapper';
import NoResults from 'components/utility/NoResults/NoResults';
import PlaylistTable from './components/PlaylistTable';
import SearchField from 'components/styled/SearchField/SearchField';
import Placeholder from 'components/data-visualization/Table/Placeholder';
import Controls from 'components/base/TableControls/TableControls';
import usePlaylistCustomers from './util/usePlaylistCustomers';
import Box from 'components/elements/Box';
import {
  CloseButton,
  GreenButton,
  PlaylistComponentWrapper,
  UnderlinedButton,
  ModalWrapper,
  DottedLine,
  IconName,
  InfoText,
  InfoTextWrapper,
  InnerTextWrapper,
  PaginationTotalWrapper,
} from './styles/Playlist.styles';
import metrics from 'maps/metrics';
import getTableHeaders from './util/getTableHeaders';
import MobileSortingControls from 'components/data-visualization/Table/Sort';
import OwnerTypesDropdown, {
  allOwnerType,
} from './components/OwnerTypesDropdown';
import Modal from 'components/utility/Modal/Modal';
import CustomizeView from 'components/CustomizeView/CustomizeView';
import P from 'components/elements/p';
import Span from 'components/elements/span';
import SearchableMenu from 'components/base/Menu/SearchableMenu';
import { ListItem } from 'components/base/TopicFiltersDropdown/CountryDropdown/ExtendedCountryDropdown';
import { getButtonWithDefaultLabel } from './components/DropdownButton';

import { ReactComponent as Close } from 'icons/icon-close.svg';

import { defaultHeaders } from './util/getTableHeaders';

import getMobileSortingOptions, {
  getSelectedMobileOption,
  defaultOptions,
  priorityOption,
} from './util/getMobileSortingOptions';
import useExpandedPlaylistCustomers from './util/useExpandedPlaylistCustomer';
import { TAsyncDispatchResult } from 'types/shared';
import { TTableHeader } from 'components/CustomizeView/CustomizeView';
import { RootState } from 'redux/rootReducer';
import { ITableHeader } from './util/getMenuItems';

import colors from 'styles/themes/colors';
import { loadConsulSettings } from 'redux/reducers/consul_parameters';
import {
  loadOrInitUserSettings,
  shouldLoadUserSettings,
} from 'redux/reducers/userSettings';

const priority_playlist_country = 'priority_playlist_country';
const priority_playlist_template = 'priority_playlist_template';

interface IPlaylistPosition {
  customerId: number;
  displayName: string;
  ownerDisplayName: string;
  playlistId: number;
  positionData: {
    periodKey: number;
    position: number;
  }[];
}

interface IPositionsData {
  countryId?: string;
  entity: {
    id: string;
    type: EntityTypes;
  };
  playlists: IPlaylistPosition[];
  regionId?: string;
  totalPlaylistCount: number;
}

interface IPlaylistPerformanceProps {
  customRegion?: string;
  fetchData: any;
  data: PromiseState<any>;
  fetchPositionData: any;
  positionData: PromiseState<IPositionsData>;
  fetchTrendData: any;
  trendData: PromiseState<any>;
  outerFilters?: any;
  subtitle?: string;
  isTopList?: boolean;
  fetchAnnotationsElements: any;
  annotationsElements: any;
  fetchAnnotationsTemplates: any;
  annotationsTemplate: any;
  postPriorityPlaylist: any;
  postPriorityPlaylistData: any;
}

/**
 * PlaylistPerformance shows a table listing the playlists a track is in and their performance.
 */
const PlaylistPerformance: React.FC<IPlaylistPerformanceProps> = (props) => {
  const {
    compareMode,
    countryIds,
    entityIds,
    entityType,
    filters: entityFilters,
    customRegionFilter,
  } = useContext(EntityContext);

  const {
    data = PromiseState.create(),
    fetchData,
    fetchPositionData,
    positionData = PromiseState.create(),
    fetchTrendData,
    trendData = PromiseState.create(),
    outerFilters,
    subtitle = '',
    isTopList,
    customRegion,
    fetchAnnotationsElements,
    annotationsElements = PromiseState.create(),
    fetchAnnotationsTemplates,
    annotationsTemplate = PromiseState.create(),
    postPriorityPlaylist,
  } = props;

  const isLargeScreen = useMedia('(min-width: 62em)');
  const isSmallScreen = useMedia('(max-width: 500px)');

  const defaultSortMetric = metrics['stream_count_this_week_to_date'];
  /**
   * Component state
   */
  const [sortMetric, setSortMetric] = useState(defaultSortMetric);
  const [sortOrder, setSortOrder] = useState('desc');
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [customerOptions, selectedCustomer, setSelectedCustomer] =
    usePlaylistCustomers();
  const [expandedItem, setExpandedId] = useState<{
    id: number | null;
    countryId?: string | null;
  }>({ id: null, countryId: null });
  const [selectedOwnerType, setOwnerType] = useState<any>(allOwnerType);
  const [isModalShown, setShowModal] = useState(false);
  const [newRequest, setNewRequest] = useState(false);
  const [isReadyToSavePriority, setReadyToSavePriority] = useState(false);
  const [isPlaylistEditing, setPlaylistEditing] = useState<null | {
    id: string;
    name: string;
  }>(null);
  const [isSuccessModalOpened, setSuccessModalOpened] =
    useState<boolean>(false);
  const [selectedCountries, selectCountries] = useState([]);
  const [isDropdownShown, setShowDropdown] = useState(false);

  const dispatch = useDispatch();
  const tableType = 'playlist';
  const customRegionValue = isTopList ? customRegion : customRegionFilter;
  const filters = isTopList ? outerFilters : entityFilters;

  // `useOffset` concats or invalidates the entity data when subsequent
  // API calls are made. For instance, when the user clicks "Show 10 more"
  // this hook will make sure the entities are added to the previous ones.
  // When we switch the sort metric, order, or filters — invalidation makes sure
  // the previous entities are flushed.
  const { offsetState, setOffset, setEntities, setInvalidate, setState } =
    useOffset({
      limit: isTopList ? 5 : 50,
      increment: isTopList ? 10 : 50,
      entitiesKey: isTopList ? 'playlists' : 'entityData.playlists',
    });
  const { limit, offset, value, increment } = offsetState;
  const expandedCustomerColor = useExpandedPlaylistCustomers(
    expandedItem.id,
    value
  );

  useEffect(() => {
    if (dispatch(shouldLoadUserSettings())) {
      dispatch(loadOrInitUserSettings());
    }
    dispatch(loadConsulSettings());
  }, [dispatch]);

  const userSettings = useSelector(
    (state: RootState): any => state.userSettings
  );

  const userRole: string = userSettings?.data?.userRole;

  const allowAnnotationRoles: string[] = useSelector(
    (state: RootState): any => state.consulEnvData
  )?.data?.RolesCanAnnotatePlaylistAsPriority;

  const canAnnotate = allowAnnotationRoles?.includes(userRole) || false;

  const toggleDropdown = (): void => {
    setShowDropdown(!isDropdownShown);
    if (!isDropdownShown) {
      logToAmplitude('Custom View Button Clicked', { type: 'Playlists' });
    }
  };

  const closeModal = (): void => {
    setPlaylistEditing(null);
    setSuccessModalOpened(false);
  };

  const saveTableHeaders = (headers: TTableHeader[]): TAsyncDispatchResult =>
    dispatch(saveToApi(headers, tableType));

  const changeLabel = (header: any): any =>
    header?.value === 'stream_wow_in_progress'
      ? { ...header, label: 'Streams WoW' }
      : header;
  const initialSavedHeaders = useSelector(
    (state: RootState): any => state.browseTable[tableType]?.data
  );
  const savedHeaders = useMemo(
    () => initialSavedHeaders?.map(changeLabel),
    [initialSavedHeaders]
  );

  const setInfiniteOffset = useCallback(() => {
    if (!offset) {
      setState({ limit: 10, offset: 5 });
    } else {
      setOffset(10);
    }
  }, [setState, setOffset, offset]);

  /**
   * Create a ref to the search field
   */
  const searchField = useRef();

  /**
   * Get the API request state
   */
  const { fulfilled, pending, rejected, reason } = data;

  /**
   * Get the exportUrl
   */
  const exportUrl = useExportURL(data, { offset: 0, limit: limit + offset });

  const extraToolbarItems = [
    {
      href: exportUrl,
      label: 'Export data',
      type: IToolbarItemType.Export,
      order: 1,
    },
  ];

  const availableHeaders = useMemo(() => {
    const initialHeaders = getTableHeaders();
    return isTopList ? initialHeaders : initialHeaders.map(changeLabel);
  }, [isTopList]);

  const availableHeadersWithDefault = [...defaultHeaders, ...availableHeaders];
  const tableHeaders = useMemo(
    () =>
      isTopList
        ? [...getTableHeaders(isTopList)]
        : savedHeaders && Array.isArray(savedHeaders) && savedHeaders.length > 0
        ? [].concat(defaultHeaders as any, savedHeaders as any)
        : availableHeadersWithDefault,
    [availableHeadersWithDefault, isTopList, savedHeaders]
  );
  const headersToChoose =
    savedHeaders && Array.isArray(savedHeaders) && savedHeaders.length > 0
      ? savedHeaders
      : availableHeaders;

  /**
   * Get the allowed filters and the customer filter for this entity
   */
  const [allowedFilters, customerFilter, isFilterLoaded] =
    useCustomerFilters('streaming');

  const selectedFilter =
    customerFilter && customerFilter.length
      ? customerFilter.slice()
      : undefined;
  /**
   * Fetch new data when context, props or state change.
   */
  useEffect(() => {
    // First check if a filter has been set, in that case we match the selected customer with the selected filter by id.
    if (customerFilter && customerFilter.length) {
      const filterId = customerFilter[0].toString();
      const cust = customerOptions?.find((customer: any) => {
        return customer.id === filterId;
      });
      if (cust) {
        setSelectedCustomer(cust);
      }
    }

    if (isFilterLoaded) {
      fetchData({
        countryIds: outerFilters?.country
          ? outerFilters?.country
          : compareMode === 'country'
          ? countryIds
          : filters?.country,
        customerFilter: outerFilters?.streaming || selectedFilter,
        entityIds,
        limit,
        offset,
        nameFilter: searchQuery,
        sortBy:
          sortMetric.value === 'priorityOnly'
            ? 'displayName'
            : sortMetric.value,
        playlistType: sortMetric.value === 'priorityOnly' ? 'priority' : 'all',
        sortOrder,
        region: filters?.region,
        isTopList,
        ownerTypeIds: selectedOwnerType.ownerTypeId,
        customRegion: customRegionValue,
      });
      setNewRequest(true);
    }
  }, [
    isFilterLoaded,
    setSelectedCustomer,
    customerOptions,
    selectedCustomer,
    compareMode,
    countryIds,
    customerFilter,
    entityIds,
    fetchData,
    filters,
    limit,
    offset,
    searchQuery,
    sortMetric,
    sortOrder,
    selectedFilter,
    outerFilters,
    isTopList,
    selectedOwnerType,
    customRegionValue,
  ]);

  useEffect(() => {
    if (newRequest && !pending && fulfilled && data?.value !== null)
      setNewRequest(false);
  }, [pending, fulfilled, data]);

  useEffect(() => {
    if (data.value) {
      const entityData = first(data.value.entityData);

      setEntities({ ...data.value, entityData });
    }
  }, [data, setEntities]);

  /*
   * When an interaction took place that will trigger a new API call, we want to
   * clear the entities in state. This is because the API should compute that and
   * the entities in state are invalid with the upcoming state.
   */
  useEffect(() => {
    // `setInvalidate` flushes the entities when the next batch of entities arrive.
    // Happens at the next `setEntities` so we don't see a skeleton loader in between.
    setInvalidate();
  }, [
    setInvalidate,
    countryIds,
    sortMetric,
    sortOrder,
    filters,
    customerFilter,
    compareMode,
    searchQuery,
    selectedCustomer,
    outerFilters,
    selectedOwnerType,
  ]);

  /** Get the latest table headers for Playlist table */
  useEffect(() => {
    dispatch(loadRecentHeaders(tableType));
  }, [dispatch]);

  /**
   * Flip the table's sorting order
   */
  const switchSortOrder = useCallback(() => {
    setSortOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
  }, []);

  /**
   * Update the metric the table is sorted by
   */
  const changeSortMetric = useCallback(
    (metric) => {
      if (sortMetric.value === metric.value) {
        return switchSortOrder();
      }

      const ascValues = ['displayName', 'customerId'];
      const shouldOrderAsc = ascValues.includes(metric.value);

      setSortOrder(shouldOrderAsc ? 'asc' : 'desc');
      setSortMetric(metric);
    },
    [sortMetric.value, switchSortOrder]
  );

  /**
   * Debounced search query setter
   */
  const doSearchQuery = useCallback(
    debounce((str: string) => setSearchQuery(str), 250),
    []
  );

  /**
   * Handler for both setting the search input contents and doing a debounced search query.
   */
  const handleSearchInput = useCallback(
    (evt) => {
      const value = evt.target.value;
      setSearchInputValue(value);
      doSearchQuery(value);
    },
    [doSearchQuery]
  );

  /**
   * Get the playlist count when data exists
   */
  const playlistCount = useMemo(() => {
    if (data.value && data.value?.entityData) {
      const d: any = first(data.value.entityData);
      if (d) {
        return d.playlist_count;
      }
    }
    if (data.value && data.value?.totalPlaylistCount) {
      return data.value.totalPlaylistCount;
    }

    return 0;
  }, [data]);

  /**
   * Should we show the NoResults component.
   */
  const showNoResults =
    fulfilled &&
    data.value &&
    searchQuery &&
    (playlistCount === 0 || !playlistCount);

  const sortingOptions = useMemo(getMobileSortingOptions, []);
  const savedOptions = useMemo(() => {
    const syncedOptions = sortingOptions.filter((option: ITableHeader) =>
      headersToChoose.find(
        (header: ITableHeader) => header.value === option.value
      )
    );

    return ([] as ITableHeader[]).concat(
      [priorityOption],
      defaultOptions,
      syncedOptions
    );
  }, [headersToChoose, sortingOptions]);

  const itemToString = (item: any): string | false => {
    if (item) {
      return `${item.value}-${item.sortOrder}`;
    }
    return false;
  };

  const getPositionsTrend = useCallback(
    (playlistId: number, expandedCountryId?: string[] | undefined): void => {
      if (isTopList) return;
      const parameters = {
        countryIds: expandedCountryId || filters?.country,
        regionIds: filters?.region,
        customerFilter: selectedFilter,
        entityIds,
        playlistIds: playlistId,
        customRegion: customRegionFilter,
        regionBreakdown: 'none',
      };
      fetchPositionData(parameters);
      fetchTrendData(parameters);
    },
    [
      entityIds,
      fetchPositionData,
      fetchTrendData,
      filters,
      selectedFilter,
      isTopList,
      customRegionFilter,
    ]
  );

  useEffect(() => {
    if (expandedItem?.id) {
      getPositionsTrend(expandedItem.id, expandedItem?.countryId);
    }
  }, [expandedItem, filters, getPositionsTrend, selectedFilter]);

  const showPagination =
    playlistCount !== null && playlistCount !== 0 && fulfilled;

  const hasDefaultHeaders = useMemo(
    () =>
      savedHeaders &&
      savedHeaders?.length > 0 &&
      availableHeaders.every((defaultItem: any) =>
        savedHeaders.find((header: any) => header.value === defaultItem.value)
      ),
    [availableHeaders, savedHeaders]
  );
  const customizeButtonText = useMemo(
    () => (hasDefaultHeaders ? 'Customize View' : 'Table has been customized'),
    [savedHeaders]
  );

  useEffect(() => {
    fetchAnnotationsElements();
    fetchAnnotationsTemplates(priority_playlist_template);
  });

  useEffect(() => {
    const isAnotationElementExist =
      annotationsElements?.fulfilled &&
      annotationsElements?.value?.find(
        (annotation: any) => annotation?.id === priority_playlist_country
      );

    if (
      annotationsTemplate?.value?.id === priority_playlist_template &&
      annotationsElements?.value &&
      isAnotationElementExist
    ) {
      setReadyToSavePriority(true);
    }
  }, [annotationsTemplate, annotationsElements?.value]);

  const markPlaylistPriority = (): void => {
    postPriorityPlaylist(isPlaylistEditing?.id, selectedCountriesIds);
    setSearchInputValue('');
    setSearchQuery('');
    setSuccessModalOpened(true);
  };

  const countries = useSelector((state: RootState) => state.countries.data);
  const countriesSorted = countries
    .map((country: any) => ({
      ...country,
      value: country?.id,
      label: country?.displayName,
    }))
    .sort((a: any, b: any): number => (a.displayName > b.displayName ? 1 : -1));
  const selectedCountriesIds = useMemo(
    () => selectedCountries.map((country: any) => country.id),
    [selectedCountries]
  );
  const handleCountryChange = (country: any): void => {
    let newCountries: any = [];
    if (
      selectedCountriesIds?.filter((countryId: any) => countryId === country.id)
        ?.length > 0
    ) {
      newCountries = selectedCountries.filter(
        (selectedCountry: any) => selectedCountry.id !== country.id
      );
    } else {
      newCountries = [
        ...(selectedCountries.length > 0 ? selectedCountries : []),
        country,
      ];
    }
    selectCountries(newCountries as any);
  };
  const clearSelectedCountries = (): void => selectCountries([]);
  const hideModal = (): void => setShowModal(false);

  return (
    <PlaylistComponentWrapper>
      <GenericComponent
        title={`${isTopList ? 'Top ' : ''}Playlists${subtitle}`}
        showIconInTitle={true}
        glossarySlug={'charts-graphs-tables/playlist-table'}
        allowedFilters={[
          'country',
          ...allowedFilters,
          'region',
          'customRegion',
        ]}
        extraToolbarItems={extraToolbarItems}
        loading={pending}
        success={fulfilled}
        error={rejected}
        isRedesignVersion={true}
        isSmallScreen={!isLargeScreen}
        customizedMode={
          !isTopList
            ? {
                isShownCustomizeButton: true,
                onClick: toggleDropdown,
                customizeButtonText,
                isCustomizedActive: !hasDefaultHeaders,
                saveToApi: saveTableHeaders,
                savedItems: savedHeaders,
                allAvailableItems: availableHeaders,
                canTurnOffAll: false,
                isCustomizedDropdownShown: isDropdownShown,
              }
            : undefined
        }
        showToolbarDropdown={false}
        showToolbarOptions={true}
      >
        <ErrorBoundary error={reason}>
          <Box position="relative">
            <Controls isSmallScreen={!isLargeScreen}>
              {!isTopList &&
                (isLargeScreen ? (
                  <>
                    <SearchField
                      handleSearch={handleSearchInput}
                      value={searchInputValue}
                      placeholder={'Search in Playlists'}
                      refObj={searchField}
                    />
                    <OwnerTypesDropdown
                      selectedOwnerType={selectedOwnerType}
                      setOwnerType={setOwnerType}
                    />
                  </>
                ) : (
                  <>
                    <SearchField
                      handleSearch={handleSearchInput}
                      value={searchInputValue}
                      placeholder={'Search in Playlists'}
                      refObj={searchField}
                      isSmallScreen
                    />
                    <MobileSortingControls
                      hide={isLargeScreen}
                      sortOrder={sortOrder}
                      handleToggle={switchSortOrder}
                      handleDropdown={changeSortMetric}
                      dropdownItems={savedOptions}
                      activeItem={getSelectedMobileOption({
                        metricKey: sortMetric.key,
                        sortOrder,
                      })}
                      itemToString={itemToString}
                      isToggleCaret={true}
                      isToggleShown={false}
                      isRoundedDropdown="all"
                    />
                    <OwnerTypesDropdown
                      selectedOwnerType={selectedOwnerType}
                      setOwnerType={setOwnerType}
                      isSmallScreen
                    />
                  </>
                ))}
            </Controls>
            <AnimatedLoadingWrapper loading={pending}>
              {showNoResults && <NoResults searchValue={searchQuery} />}
              {!showNoResults && (
                <>
                  <PlaylistTable
                    data={value}
                    expandedId={expandedItem?.id}
                    expandedCountryId={expandedItem?.countryId}
                    graphColor={expandedCustomerColor}
                    headers={tableHeaders}
                    columns={headersToChoose}
                    sortMetric={sortMetric}
                    sortOrder={sortOrder}
                    setSortMetric={setSortMetric}
                    setSortOrder={setSortOrder}
                    changeSortMetric={changeSortMetric}
                    isLargeScreen={isLargeScreen}
                    getPositionsTrend={getPositionsTrend}
                    positionData={positionData}
                    trendData={trendData}
                    setExpanded={
                      isTopList && isLargeScreen
                        ? (): null => null
                        : setExpandedId
                    }
                    priorityPlaylistsShown={
                      !pending &&
                      fulfilled &&
                      !newRequest &&
                      isReadyToSavePriority
                    }
                    setPlaylistEditing={setPlaylistEditing}
                    canAnnotate={canAnnotate}
                  />

                  {showPagination && (
                    <Pagination
                      handleClick={isTopList ? setInfiniteOffset : setOffset}
                      data={value}
                      type="playlists"
                      offset={increment}
                      isWideButton
                    />
                  )}
                  {showPagination && !isLargeScreen && (
                    <PaginationTotalWrapper>
                      <Pagination
                        data={value}
                        type="playlists"
                        offset={increment}
                        showOnlyTotal
                      />
                    </PaginationTotalWrapper>
                  )}
                  {playlistCount === 0 && fulfilled && (
                    <Placeholder
                      isSmallScreen={!isLargeScreen}
                      entityType={entityType}
                      type="playlist"
                      isTopList={isTopList}
                      marginTop="64px"
                    />
                  )}
                  <InfoTextWrapper>
                    <div style={{ display: 'flex' }}>
                      <InnerTextWrapper>
                        <span className="disc"></span>
                        <InfoText>A Priority Playlist</InfoText>
                      </InnerTextWrapper>
                      <InnerTextWrapper>
                        <span className="rhombus"></span>
                        <InfoText>
                          Priority status has been requested for this playlist
                        </InfoText>
                      </InnerTextWrapper>
                    </div>
                    {showPagination && isLargeScreen && (
                      <Pagination
                        data={value}
                        type="playlists"
                        offset={increment}
                        showOnlyTotal
                      />
                    )}
                  </InfoTextWrapper>
                  <P>
                    &#42; A Priority Playlist will be added to the Priority
                    Playlists Activity table.
                  </P>
                </>
              )}
            </AnimatedLoadingWrapper>
          </Box>
        </ErrorBoundary>

        {isModalShown && (
          <Modal hide={hideModal} canClickOutside={true} isNarrow={true}>
            <CustomizeView
              title="Browse Playlists"
              hideModal={hideModal}
              saveToApi={saveTableHeaders}
              savedItems={headersToChoose}
              allAvailableItems={availableHeaders}
            />
          </Modal>
        )}

        {isPlaylistEditing?.id && (
          <Modal
            hide={closeModal}
            maxWidth="500px"
            margin={isSmallScreen ? '0' : undefined}
          >
            <ModalWrapper>
              <Box
                padding={isSmallScreen ? '0' : '2rem'}
                textAlign="center"
                fontSize="18px"
                style={
                  isSmallScreen
                    ? { height: '100vh', width: '100vw', display: 'flex' }
                    : undefined
                }
              >
                <CloseButton
                  display="block"
                  marginLeft="auto"
                  padding="0"
                  onClick={closeModal}
                >
                  <Close />
                </CloseButton>

                <Box
                  padding={`0 ${isSmallScreen ? 1 : 2}rem`}
                  style={isSmallScreen ? { alignSelf: 'center' } : undefined}
                >
                  {isSuccessModalOpened ? (
                    <>
                      <IconCheck
                        style={{
                          color: '#0BD6A7',
                          transform: 'scale(3.5)',
                          margin: '20px 0 22px 0',
                        }}
                      />
                      <P>
                        <Span fontWeight="600">{isPlaylistEditing?.name}</Span>{' '}
                        has been successfully marked as a Priority Playlist with{' '}
                        <Span fontWeight="600">
                          {selectedCountries
                            ?.map((country: any) => country.displayName)
                            .join(', ')}
                        </Span>{' '}
                        set as a territory.
                      </P>
                      <span className="rhombus"></span>
                      <DottedLine />
                      <span className="disc"></span>
                      <br /> <IconName>Requested</IconName>
                      <IconName style={{ marginLeft: '30px' }}>
                        Set as Priority
                      </IconName>
                      {isSmallScreen ? (
                        <P marginTop="10px">
                          Once a playlists is requested it will turn{' '}
                          <Span fontWeight="600" color="#F0CC00">
                            yellow
                          </Span>
                          . When it is set as Priority it will turn{' '}
                          <Span fontWeight="600" color="#317CE2">
                            blue
                          </Span>
                          .
                        </P>
                      ) : (
                        <>
                          <P marginTop="10px">
                            Once a playlists is requested it will turn{' '}
                            <Span fontWeight="600" color="#F0CC00">
                              yellow
                            </Span>
                            .
                          </P>
                          <P>
                            When it is set as Priority it will turn{' '}
                            <Span fontWeight="600" color="#317CE2">
                              blue
                            </Span>
                            .
                          </P>
                        </>
                      )}
                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <GreenButton
                          type="button"
                          width="200px"
                          onClick={closeModal}
                          style={{ marginTop: '30px' }}
                        >
                          OK
                        </GreenButton>
                      </Box>
                    </>
                  ) : (
                    <>
                      <P
                        paddingTop="1.2rem"
                        paddingBottom="1.2rem"
                        color={colors.NightBlue}
                      >
                        Are you sure you want to mark{' '}
                        <Span fontWeight="600">{isPlaylistEditing?.name}</Span>{' '}
                        as a Priority Playlist?
                      </P>
                      <P paddingBottom="1.8rem" color={colors.NightBlue}>
                        This change will affect the Priority Playlist table and{' '}
                        <Span fontWeight="600">
                          will be visible to all WMG users.
                        </Span>
                      </P>

                      <Box width="286px" margin="0 auto 10px">
                        <SearchableMenu
                          items={countriesSorted}
                          selectedItem={selectedCountries[0] || null}
                          selectedItems={selectedCountriesIds}
                          onChange={handleCountryChange}
                          itemToString={(item: any): string => item?.id}
                          Button={getButtonWithDefaultLabel(
                            clearSelectedCountries,
                            'Select Playlist Territory'
                          )}
                          Item={ListItem}
                          maxHeight={isSmallScreen ? '13em' : '14em'}
                          isMultipleSelection
                        />
                      </Box>

                      <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                      >
                        <GreenButton
                          type="button"
                          width="286px"
                          onClick={markPlaylistPriority}
                          disabled={selectedCountries.length === 0}
                        >
                          Save changes
                        </GreenButton>
                        <UnderlinedButton type="button" onClick={closeModal}>
                          Cancel
                        </UnderlinedButton>
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            </ModalWrapper>
          </Modal>
        )}
      </GenericComponent>
    </PlaylistComponentWrapper>
  );
};

export default connect<IPlaylistPerformanceProps>(() => ({
  fetchData: (args: any): any => ({
    data: getPlaylistTableURL(args),
  }),
  fetchPositionData: (parameters: IPlaylistTrendURLArgs): any => ({
    positionData: getPlaylistPositionsTrendURL(parameters),
  }),
  fetchTrendData: (parameters: IPlaylistTrendURLArgs): any => ({
    trendData: getPlaylistTrendURL(parameters),
  }),
  fetchAnnotationsElements: (): any => ({
    annotationsElements: '/api/annotations/elements',
  }),
  fetchAnnotationsTemplates: (templateId): any => ({
    annotationsTemplate: `/api/annotations/templates/${templateId}`,
  }),
  postPriorityPlaylist: (
    playlistId: string,
    countryIds: string,
    successMethod: () => void,
    errorMethod?: () => void
  ): any => ({
    postPriorityPlaylistData: {
      url: `/api/annotations/priority_playlist/${playlistId}/templates/${priority_playlist_template}`,
      method: 'POST',
      body: JSON.stringify({
        elements: [
          {
            id: priority_playlist_country,
            values: countryIds,
          },
        ],
      }),
      catch: (): void => errorMethod && errorMethod(),
      then: successMethod,
    },
  }),
}))(PlaylistPerformance) as any;
